import create from "zustand";
import produce from "immer";

import { useUserStore } from "base/stores";
import { generateId } from "base/utils";

import { CommentService } from "../service/comment.service";
import { commentLiveSync } from "../livesync/comment";
import { ERROR_CODE } from "../utils/constant";

const createWithImmer = (storeCreator) =>
  create((originalSet, originalGet) =>
    storeCreator((fn) => originalSet(produce(fn)), originalGet)
  );

export const useCommentStore = createWithImmer((set, get) => ({
  postMapping: {},
  commentReactionMapping: {},
  commentReplyMapping: {},
  replyReactionMapping: {},
  eventIds: [],

  getComments: async function getComments(params) {
    const instanceId = params.instanceId;
    const [promise] = CommentService.getComments(params);
    const response = await promise();
    set((state) => {
      state.postMapping[instanceId] = {
        comments: response.Comments,
        reactions: response.Reactions,
        totalComments: response.TotalComments,
        showAllComments: true,
        fromNotification: false,
        noCommentFound: false
      };
      response.Comments.forEach((comment) => {
        state.commentReactionMapping[comment._id] = comment.Reactions;
      });
    });
  },

  getCommentsBatch: function getComments(params) {
    const [promise, cancelPromise] = CommentService.getCommentsBatch(params);
    const getCommentsBatchResponse = promise().then((response) => {
      set((state) => {
        for (const instanceId of params.postIds) {
          const commentData = response[instanceId] || {};
          state.postMapping[instanceId] = {
            comments: commentData.Comments || [],
            reactions: commentData.Reactions || [],
            totalComments: commentData.TotalComments || 0,
            showAllComments: true,
            fromNotification: false,
            noCommentFound: false
          };
          commentData.Comments?.forEach((comment) => {
            state.commentReactionMapping[comment._id] = comment.Reactions;
          });
        }
      });
    });
    return [getCommentsBatchResponse, cancelPromise];
  },

  getComment: async function getComment(
    params,
    postNotification,
    focusedNotification
  ) {
    const instanceId = params.instanceId;
    if (
      (postNotification &&
        postNotification.notificationData[0].Properties &&
        postNotification.notificationData[0].Properties.CommentId) ||
      (focusedNotification &&
        focusedNotification.Properties &&
        focusedNotification.Properties.CommentId)
    ) {
      const [promise] = CommentService.getComment({
        ...params,
        commentId: postNotification
          ? postNotification.notificationData[0].Properties.CommentId
          : focusedNotification.Properties.CommentId
      });
      try {
        const response = await promise();
        set((state) => {
          state.postMapping[instanceId] = {
            ...state.postMapping[instanceId],
            comments: [response.Comment],
            totalComments: response.TotalComments,
            showAllComments: true,
            noCommentFound: false
          };
          state.commentReactionMapping[response.Comment._id] =
            response.Comment.Reactions;
        });
      } catch (e) {
        if (e.error_code === ERROR_CODE.KISSFLOW_ERROR_06501) {
          set((state) => {
            state.postMapping[instanceId] = {
              ...state.postMapping[instanceId],
              noCommentFound: true
            };
          });
        }
      }
      set((state) => {
        state.postMapping[instanceId] = {
          ...state.postMapping[instanceId],
          fromNotification: {
            commentId: postNotification
              ? postNotification.notificationData[0].Properties.CommentId
              : focusedNotification.Properties.CommentId,
            replyId: postNotification
              ? postNotification.notificationData[0].Properties.ReplyId
              : focusedNotification.Properties.ReplyId
          }
        };
      });
    }
  },

  getMainCommentsPrint: async function getMainCommentsPrint(params) {
    const [promise] = CommentService.getAllComments(params);
    const response = await promise();
    return response;
  },

  getRepliesSubComment: async function getRepliesSubComment(params, commentId) {
    const [promise] = CommentService.getReplyComments({
      ...params,
      commentId
    });
    const response = await promise();
    return response;
  },

  getReaction: async function getReaction(params) {
    const instanceId = params.instanceId;

    const [promise] = CommentService.getReaction(params);
    const response = await promise();
    set((state) => {
      state.postMapping[instanceId] = {
        ...state.postMapping[instanceId],
        reactions: response
      };
    });
  },

  reactToPost: async function reactToPost(
    params,
    type,
    currentReactionType,
    isCurrentUserReacted
  ) {
    const instanceId = params.instanceId;
    const { currentUser } = useUserStore.getState();

    let currentReaction = currentReactionType.image;
    let reactionType = type || currentReactionType.Name;
    if (
      !isCurrentUserReacted &&
      ["Like-outline", "LikeOutlineBlack"].includes(currentReaction)
    ) {
      set((state) => {
        if (state.postMapping[instanceId]) {
          state.postMapping[instanceId].reactions.push({
            Name: currentUser.Name,
            ReactionType: reactionType,
            _id: currentUser._id
          });
        } else {
          state.postMapping[instanceId] = {
            comments: [],
            reactions: [
              {
                Name: currentUser.Name,
                ReactionType: reactionType,
                _id: currentUser._id
              }
            ]
          };
        }
      });
      const [promise] = CommentService.postReaction({
        ...params,
        data: { ReactionType: reactionType }
      });
      await promise();
    } else if (isCurrentUserReacted && currentReaction === reactionType) {
      set((state) => {
        state.postMapping[instanceId].reactions = state.postMapping[
          instanceId
        ].reactions.filter((user) => user._id !== currentUser._id);
      });
      const [promise] = CommentService.unlikeReaction({
        ...params,
        data: { ReactionType: reactionType }
      });
      await promise();
    } else {
      set((state) => {
        let reactedUser = state.postMapping[instanceId].reactions.find(
          (user) => user._id === currentUser._id
        );
        reactedUser["ReactionType"] = reactionType;
      });
      const [promise] = CommentService.postReaction({
        ...params,
        data: { ReactionType: reactionType }
      });
      await promise();
    }
  },

  updateComment: async function updateComment(params, value) {
    const instanceId = params.instanceId;
    const eventId = generateId("Ev");
    get().addEventId(eventId);
    const [promise] = CommentService.updateComment({
      ...params,
      commentId: value.id,
      data: { ...value.data, EventId: eventId }
    });
    await promise();

    set((state) => {
      let commentIndex = state.postMapping[instanceId].comments.findIndex(
        (comment) => comment._id === value.id
      );
      state.postMapping[instanceId].comments[commentIndex].Content =
        value.data.Content;
      state.postMapping[instanceId].comments[commentIndex].AtMention =
        value.data.AtMention;
    });
  },

  deleteComment: async function deleteComment(params, value) {
    const instanceId = params.instanceId;
    const [promise] = CommentService.deleteComment({
      ...params,
      commentId: value.id
    });
    await promise();

    set((state) => {
      state.postMapping[instanceId].comments = state.postMapping[
        instanceId
      ].comments.filter((comment) => comment._id !== value.id);
    });
  },

  createNewReply: async function createNewReply(params, value, cb) {
    const commentId = value.id;
    const eventId = generateId("Ev");
    get().addEventId(eventId);
    const [promise] = CommentService.createReplycomment({
      ...params,
      commentId: value.id,
      data: { ...value.data, EventId: eventId }
    });
    const response = await promise();
    set((state) => {
      let commentIndex = state.postMapping[
        params.instanceId
      ].comments.findIndex((comment) => comment._id === commentId);
      state.postMapping[params.instanceId].comments[
        commentIndex
      ].TotalReplies += 1;
    });
    if (!cb) {
      set((state) => {
        if (state.commentReplyMapping[commentId]) {
          state.commentReplyMapping[commentId].push(response);
          state.replyReactionMapping[response._id] = [];
        } else {
          state.commentReplyMapping[commentId] = [response];
          state.replyReactionMapping[response._id] = [];
        }
      });
    }

    if (cb) {
      cb();
    }
  },

  onShowAllComments: async function onShowAllComments(params) {
    const instanceId = params.instanceId;
    const [promise] = CommentService.getAllComments(params);
    const response = await promise();

    set((state) => {
      if (!state.postMapping[instanceId]) {
        state.postMapping[instanceId] = {
          comments: response.Comments,
          reactions: response.Reactions,
          totalComments: response.TotalComments,
          showAllComments: true,
          fromNotification: false,
          noCommentFound: false
        };
      } else {
        state.postMapping[instanceId] = {
          comments: response.Comments,
          reactions: response.Reactions,
          totalComments: response.TotalComments,
          showAllComments: false
        };
      }
      response.Comments.forEach((comment) => {
        state.commentReactionMapping[comment._id] = comment.Reactions;
      });
    });
  },

  createNewComment: async function createNewComment(params, value) {
    const instanceId = params.instanceId;
    const eventId = generateId("Ev");
    get().addEventId(eventId);
    CommentService.createComments(
      { ...params, data: { ...value, EventId: eventId } },
      (err, response) => {
        set((state) => {
          if (state.postMapping[instanceId]) {
            state.postMapping[instanceId].comments.push(response);
            state.postMapping[instanceId].totalComments += 1;
          } else {
            state.postMapping[instanceId] = {
              comments: [response],
              reactions: [],
              totalComments: 1,
              showAllComments: true,
              fromNotification: false,
              noCommentFound: false
            };
          }
          state.commentReactionMapping[response._id] = [];
        });
      }
    );
  },

  reactToComment: async function reactToComment(
    params,
    isCurrentUserReacted,
    currentReactionType,
    ReactionType,
    comment,
    cb
  ) {
    const { currentUser } = useUserStore.getState();
    const commentId = comment._id;
    if (isCurrentUserReacted && currentReactionType === ReactionType) {
      set((state) => {
        state.commentReactionMapping[commentId] = (
          state.commentReactionMapping[commentId] || []
        ).filter((name) => name._id !== currentUser._id);
      });

      try {
        const [promise] = await CommentService.unlikeCommentReaction({
          ...params,
          commentId,
          data: { ReactionType }
        });
        await promise();
      } catch (e) {
        console.info("Error in unlike comment reaction", e);
        cb({
          currentReactionType: ReactionType,
          isCurrentUserReacted: true,
          showReactionData: false
        });
      }
    } else {
      if (!isCurrentUserReacted) {
        set((state) => {
          state.commentReactionMapping[commentId].push({
            Name: currentUser.Name,
            ReactionType,
            _id: currentUser._id
          });
        });
      } else {
        set((state) => {
          state.commentReactionMapping[commentId].find(
            (reaction) => reaction.Name === currentUser.Name
          ).ReactionType = ReactionType;
        });
      }

      try {
        const [promise] = await CommentService.postCommentReaction({
          ...params,
          commentId,
          data: { ReactionType }
        });
        await promise();
      } catch (e) {
        console.info("Error in comment reaction", e);
        cb({
          currentReactionType: "Like",
          isCurrentUserReacted: false
        });
      }
    }
  },

  getReplies: function getReplies(params, commentId) {
    const [promise, cancelPromise] = CommentService.getReplyComments({
      ...params,
      commentId
    });
    const response = promise().then((replies) => {
      set((state) => {
        let something = {};
        state.commentReplyMapping[commentId] = replies;
        replies.forEach((reply) => {
          something[reply._id] = reply.Reactions;
          state.replyReactionMapping[reply._id] = reply.Reactions;
        });
      });
    });
    return [response, cancelPromise];
  },

  updateReply: async function updateReply(params, value) {
    const commentId = value.commentId;
    const eventId = generateId("Ev");
    get().addEventId(eventId);
    const [promise] = CommentService.updateReply({
      ...params,
      replyId: value.id,
      data: { ...value.data, EventId: eventId },
      commentId: value.commentId
    });
    await promise();
    set((state) => {
      let replyIndex = state.commentReplyMapping[commentId].findIndex(
        (reply) => reply._id === value.id
      );
      state.commentReplyMapping[commentId][replyIndex].Content =
        value.data.Content;
      state.commentReplyMapping[commentId][replyIndex].AtMention =
        value.data.AtMention;
    });
  },

  deleteReply: async function deleteReply(params, value) {
    const commentId = value.commentId;
    const [promise] = CommentService.deleteReply({
      ...params,
      replyId: value.id,
      commentId: value.commentId
    });
    await promise();
    set((state) => {
      state.commentReplyMapping[commentId] = state.commentReplyMapping[
        commentId
      ].filter((reply) => reply._id !== value.id);
    });
  },

  reactToReply: async function reactToReply(
    params,
    isCurrentUserReacted,
    currentReactionType,
    ReactionType,
    comment,
    cb
  ) {
    const { currentUser } = useUserStore.getState();
    const replyId = comment._id;

    if (isCurrentUserReacted && currentReactionType === ReactionType) {
      set((state) => {
        state.replyReactionMapping[replyId] = (
          state.replyReactionMapping[replyId] || []
        ).filter((name) => name._id !== currentUser._id);
      });

      try {
        const [promise] = await CommentService.unlikeReplyReaction({
          ...params,
          commentId: comment.CommentId,
          replyId: comment._id,
          data: { ReactionType }
        });
        await promise();
      } catch (e) {
        console.info("Error in unlike reply reaction", e);
        cb({
          currentReactionType: ReactionType,
          isCurrentUserReacted: true,
          showReactionData: false
        });
      }
    } else {
      if (!isCurrentUserReacted) {
        set((state) => {
          state.replyReactionMapping[replyId].push({
            Name: currentUser.Name,
            ReactionType,
            _id: currentUser._id
          });
        });
      } else {
        set((state) => {
          state.replyReactionMapping[replyId].find(
            (reaction) => reaction.Name === currentUser.Name
          ).ReactionType = ReactionType;
        });
      }

      try {
        const [promise] = await CommentService.postReplyReaction({
          ...params,
          commentId: comment.CommentId,
          replyId: comment._id,
          data: { ReactionType }
        });
        await promise();
      } catch (e) {
        console.info("Error in reply reaction", e);
        cb({
          currentReactionType: "Like",
          isCurrentUserReacted: false
        });
      }
    }
  },
  addEventId: (newEventId) => {
    set((state) => {
      state.eventIds.push(newEventId);
    });
  },

  liveSync: async (response) => {
    let {
      eventIds,
      postMapping,
      commentReactionMapping,
      commentReplyMapping,
      replyReactionMapping
    } = get();
    if (eventIds.includes(response.EventId)) return;
    let { newState } = await commentLiveSync(
      {
        postMapping,
        commentReactionMapping,
        commentReplyMapping,
        replyReactionMapping
      },
      response
    );
    if (!newState) return;
    set(() => ({
      postMapping: newState.postMapping,
      commentReactionMapping: newState.commentReactionMapping,
      commentReplyMapping: newState.commentReplyMapping,
      replyReactionMapping: newState.replyReactionMapping
    }));
  }
}));
